/**
 * Languages the app ships translations for — the full set the language picker
 * can offer. Which of them a given hospital actually offers comes from
 * `hospitals.config->'appSupportedLanguages'` (see `toLanguageOptions`).
 */
export const SUPPORTED_LANGUAGES = [
  { value: 'pt-BR', label: 'Português' },
  { value: 'en-US', label: 'English' },
  { value: 'es-419', label: 'Español' },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['value'];

export type LanguageOption = typeof SUPPORTED_LANGUAGES[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'pt-BR';

/** localStorage key used to persist the selected language. */
export const LANGUAGE_STORAGE_KEY = 'inpatientPatientPwaLanguage';

/**
 * Maps an app language tag to the corresponding moment.js locale id.
 * moment ships `en` by default; we import `en-gb`, `es` and `pt-br` locales.
 */
export const toMomentLocale = (language: string): string => {
  switch (language) {
    // American English is moment's built-in locale, so there is nothing to
    // import for it — unlike `en-gb`, it needs no locale file.
    case 'en-US':
      return 'en';
    case 'es-419':
      return 'es';
    case 'pt-BR':
    default:
      return 'pt-br';
  }
};

/**
 * Narrows a detected language tag down to one the app actually offers.
 *
 * Browsers report any number of regional variants (`es`, `es-MX`, `es-AR`, …),
 * and devices that picked Spanish before the es-419 rename have `es-ES`
 * persisted in localStorage. i18next cannot bridge that on its own:
 * `supportedLngs` holds regional tags, and `nonExplicitSupportedLngs` compares
 * the bare language part (`es`) against that list (`es-419`), so it never
 * matches. Normalizing at detection time is what does — and it also rewrites
 * the persisted value on the next `languageChanged`, so each device self-heals
 * once.
 */
export const normalizeDetectedLanguage = (
  language: string,
): SupportedLanguage => {
  switch (language.split('-')[0]) {
    case 'es':
      return 'es-419';
    case 'en':
      return 'en-US';
    case 'pt':
      return 'pt-BR';
    default:
      return DEFAULT_LANGUAGE;
  }
};

/**
 * Narrows a hospital's configured language tags down to the picker options the
 * app can actually render.
 *
 * The tags come straight from `hospitals.config->'appSupportedLanguages'`, so
 * they may carry regional variants the app does not ship (`es-ES`, `en-GB`) or
 * languages it has no translations for. Entries are matched on the bare
 * language part — the same leniency `normalizeDetectedLanguage` applies to the
 * browser's tags — and unknown ones are dropped rather than folded into the
 * pt-BR fallback.
 *
 * An absent, empty or fully unmatched config yields no options at all: the
 * hospital offers no choice, so the picker is hidden and the app stays on
 * `DEFAULT_LANGUAGE` (see `LanguageProvider`).
 */
export const toLanguageOptions = (
  languages?: ReadonlyArray<string> | null,
): ReadonlyArray<LanguageOption> => {
  if (!languages?.length) return [];

  const configured = new Set(
    languages.map((language) => language.split('-')[0]),
  );

  return SUPPORTED_LANGUAGES.filter((option) =>
    configured.has(option.value.split('-')[0]),
  );
};

/**
 * Locale keys to try, in order, when resolving translated content — both for
 * the `Accept-Language` header sent to the API and for the client-side
 * resolution of locale maps nested in JSONB columns.
 *
 * Sending the whole chain makes the pt-BR fallback explicit on the wire instead
 * of leaning on the hard-coded one inside the API's `resolveI18n`, and keeps the
 * two resolution paths reading from a single source. Add an entry here (rather
 * than at the call sites) if a language ever needs an intermediate locale
 * between itself and the fallback.
 */
export const toContentLocales = (language: string): Array<string> => [
  ...new Set([language, DEFAULT_LANGUAGE]),
];

/**
 * Renders the content locale chain as an `Accept-Language` header value, e.g.
 * `es-419, pt-BR;q=0.9`.
 *
 * The API's `parseAcceptLanguage` strips the `q` weights and preserves the
 * header order as-is rather than sorting by them, so the order here is what
 * decides resolution; the weights are emitted only to keep the header
 * well-formed for anything else on the path.
 */
export const toAcceptLanguage = (language: string): string =>
  toContentLocales(language)
    .map((locale, index) =>
      index === 0 ? locale : `${locale};q=${(1 - index / 10).toFixed(1)}`,
    )
    .join(', ');
